import React, { useCallback, useMemo, useState } from "react";

import { useFormikContext } from "formik";
import { JournalEntryDrawerFormikConfig } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer";
import { PlainModal } from "@design-system/Modal/Modal";
import { Field } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { getUTCDateForPST } from "@common/helpers/date";
import {
    ManualJournalRecurringPeriod,
    MonthRecurrenceType,
} from "@common/schemas/domains/accounting/manualJournalRecurringRule";
import { formatRecurringRuleLabel } from "@common/helpers/recurringJournalEntries";
import classnames from "classnames";
import { RECURRING_PERIOD } from "@components/accounting/journalEntryDrawer/constants";
import { getFirstOccurrenceDate } from "@components/accounting/journalEntryDrawer/helpers";
import { formatDate } from "@helpers/date";
import styles from "./JournalEntryRecurrenceSelect.module.scss";
import {
    RecurrenceModalContent,
    RecurrenceFormValues,
} from "./RecurrenceModalContent";

type RecurrenceSelectValue =
    | ManualJournalRecurringPeriod
    | "custom"
    | "disabled";

interface Props {
    disabled?: boolean;
}

export const JournalEntryRecurrenceSelect: React.FC<Props> = ({ disabled }) => {
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, setFieldValue } = formikContext;
    const [showCustomModal, setShowCustomModal] = useState(false);

    const emptyAllFields = useCallback(() => {
        setFieldValue("journalEntry.recurringRule.interval", undefined);
        setFieldValue("journalEntry.recurringRule.period", undefined);
        setFieldValue("journalEntry.recurringRule.endDate", undefined);
        setFieldValue(
            "journalEntry.recurringRule.maxOccurrenceCount",
            undefined,
        );
        setFieldValue("journalEntry.recurringRule.weekDay", undefined);
        setFieldValue(
            "journalEntry.recurringRule.monthRecurrenceType",
            undefined,
        );
        setFieldValue("journalEntry.recurringRule.month", undefined);
        setFieldValue("journalEntry.recurringRule.dateOfMonth", undefined);
    }, [setFieldValue]);

    const handleCustom = useCallback(() => {
        setShowCustomModal(true);
    }, [setShowCustomModal]);

    const handleSelect = useCallback(
        (value: RecurrenceSelectValue) => {
            if (value === "custom") {
                return handleCustom();
            }

            if (value === "disabled") {
                return emptyAllFields();
            }

            emptyAllFields();
            setFieldValue("journalEntry.recurringRule.interval", 1);
            setFieldValue("journalEntry.recurringRule.period", value);
            setFieldValue("journalEntry.recurringRule.endDate", null);
            setFieldValue(
                "journalEntry.recurringRule.maxOccurrenceCount",
                null,
            );

            const date = getUTCDateForPST(new Date(values.journalEntry.date));
            if (value === ManualJournalRecurringPeriod.WEEKLY) {
                setFieldValue(
                    "journalEntry.recurringRule.weekDay",
                    date.getDay(),
                );
            } else if (value === ManualJournalRecurringPeriod.MONTHLY) {
                setFieldValue(
                    "journalEntry.recurringRule.monthRecurrenceType",
                    MonthRecurrenceType.DAY,
                );
                setFieldValue(
                    "journalEntry.recurringRule.dateOfMonth",
                    date.getDate(),
                );
            } else if (value === ManualJournalRecurringPeriod.YEARLY) {
                setFieldValue(
                    "journalEntry.recurringRule.month",
                    date.getMonth(),
                );
                setFieldValue(
                    "journalEntry.recurringRule.dateOfMonth",
                    date.getDate(),
                );
            }
        },
        [setFieldValue, handleCustom, emptyAllFields, values.journalEntry.date],
    );

    const handleCancel = useCallback(() => {
        setShowCustomModal(false);
    }, []);

    const handleSubmitCustom = useCallback(
        (formValues: RecurrenceFormValues) => {
            if (formValues.period && formValues.interval) {
                setFieldValue(
                    "journalEntry.recurringRule.interval",
                    formValues.interval,
                );
                setFieldValue(
                    "journalEntry.recurringRule.period",
                    formValues.period,
                );
                setFieldValue(
                    "journalEntry.recurringRule.endDate",
                    formValues.endDate,
                );
                setFieldValue(
                    "journalEntry.recurringRule.maxOccurrenceCount",
                    formValues.maxOccurrenceCount,
                );
                setFieldValue(
                    "journalEntry.recurringRule.weekDay",
                    formValues.weekDay,
                );
                setFieldValue(
                    "journalEntry.recurringRule.monthRecurrenceType",
                    formValues.monthRecurrenceType,
                );
                setFieldValue(
                    "journalEntry.recurringRule.month",
                    formValues.month,
                );
                setFieldValue(
                    "journalEntry.recurringRule.dateOfMonth",
                    formValues.dateOfMonth,
                );

                const firstOccurrenceDate = getFirstOccurrenceDate(
                    formValues,
                    values.journalEntry.date,
                );
                setFieldValue(
                    "journalEntry.date",
                    formatDate(firstOccurrenceDate),
                );
            }
            setShowCustomModal(false);
        },
        [setFieldValue, values.journalEntry.date],
    );

    const labelToShow = useMemo(() => {
        const emptyState = disabled ? "" : "Add recurrence";
        const rule = values.journalEntry.recurringRule;
        if (!rule?.period || !rule?.interval) {
            return emptyState;
        }
        const label = formatRecurringRuleLabel(rule);
        return label || emptyState;
    }, [values.journalEntry, disabled]);

    const recurringAndAdditionalOptions = useMemo(
        (): Array<PickerItemData<RecurrenceSelectValue>> => [
            { label: "None", value: "disabled" },
            ...RECURRING_PERIOD.map(({ value, label }) => ({
                label,
                value,
            })),
            { label: "Custom", value: "custom" },
        ],
        [],
    );

    const selectedRecurrenceValue = useMemo(() => {
        const period = values.journalEntry.recurringRule?.period;
        return period ?? null;
    }, [values.journalEntry.recurringRule?.period]);

    return (
        <>
            <Field
                label="Recurrence"
                control="button"
                className={styles.container}
            >
                <Picker.Select
                    disabled={disabled}
                    value={selectedRecurrenceValue}
                    onValueChange={(value) => {
                        if (!value) {
                            return;
                        }
                        handleSelect(value);
                    }}
                    items={recurringAndAdditionalOptions}
                    placeholder={labelToShow}
                    renderValue={() => labelToShow}
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="xl"
                            block
                            className={classnames({
                                [styles.notSelected]:
                                    !values.journalEntry.recurringRule
                                        ?.period ||
                                    !values.journalEntry.recurringRule
                                        ?.interval,
                            })}
                        />
                    }
                />
            </Field>

            <PlainModal show={showCustomModal} onHide={handleCancel}>
                <RecurrenceModalContent
                    initialValues={{
                        interval:
                            values.journalEntry.recurringRule?.interval ?? 1,
                        period: values.journalEntry.recurringRule?.period,
                        weekDay: values.journalEntry.recurringRule?.weekDay,
                        month: values.journalEntry.recurringRule?.month,
                        dateOfMonth:
                            values.journalEntry.recurringRule?.dateOfMonth,
                        monthRecurrenceType:
                            values.journalEntry.recurringRule
                                ?.monthRecurrenceType,
                        endDate: values.journalEntry.recurringRule?.endDate,
                        maxOccurrenceCount:
                            values.journalEntry.recurringRule
                                ?.maxOccurrenceCount,
                    }}
                    disabled={disabled}
                    journalEntryDate={values.journalEntry.date}
                    onSave={handleSubmitCustom}
                    onCancel={handleCancel}
                />
            </PlainModal>
        </>
    );
};
